import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import api from '../services/api'
import Header from '../components/Header'

const SuiviExecutionPage = () => {
  const { numero } = useParams()
  const navigate = useNavigate()
  const { utilisateur, deconnexion } = useAuth()
  const monRole = utilisateur?.role
  const dossierId = React.useRef(null) // id numérique résolu une fois

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showFormRapport, setShowFormRapport] = useState(false)
  const [showFormDateLimite, setShowFormDateLimite] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const [formRapport, setFormRapport] = useState({
    pourcentage_avancement: 0,
    commentaire: '',
    problemes_signales: ''
  })
  const [formDateLimite, setFormDateLimite] = useState('')

  const ROLES_PEUVENT_SAISIR = ['prepose', 'responsable_preposes', 'maintenance', 'admin', 'super_admin']
  const ROLES_PEUVENT_GERER = ['service_achats', 'responsable_achats', 'admin', 'super_admin']

  const chargerDonnees = async () => {
    try {
      setLoading(true)
      // Résoudre l'id numérique la première fois via by-numero
      if (!dossierId.current) {
        const d = await api.get(`/achats/dossiers/by-numero/${encodeURIComponent(numero)}`)
        dossierId.current = d.data.id
      }
      const res = await api.get(`/achats/${dossierId.current}/suivi`)
      setData(res.data)
      if (res.data.dossier?.pourcentage_avancement) {
        setFormRapport(f => ({ ...f, pourcentage_avancement: res.data.dossier.pourcentage_avancement }))
      }
    } catch(e) {
      setError(e.response?.data?.message || 'Erreur chargement')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { chargerDonnees() }, [numero])

  const handleSoumettreRapport = async () => {
    if (!formRapport.commentaire.trim()) {
      alert('Le commentaire est obligatoire')
      return
    }
    setSubmitting(true)
    try {
      await api.post(`/achats/${dossierId.current}/suivi/rapports`, formRapport)
      setShowFormRapport(false)
      setFormRapport({ pourcentage_avancement: 0, commentaire: '', problemes_signales: '' })
      await chargerDonnees()
    } catch(e) {
      alert(e.response?.data?.message || 'Erreur')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDefinirDateLimite = async () => {
    if (!formDateLimite) { alert('Date requise'); return }
    setSubmitting(true)
    try {
      await api.put(`/achats/${dossierId.current}/suivi/date-limite`, { date_limite_execution: formDateLimite })
      setShowFormDateLimite(false)
      await chargerDonnees()
    } catch(e) {
      alert(e.response?.data?.message || 'Erreur')
    } finally {
      setSubmitting(false)
    }
  }

  const handleConfirmerLivraison = async (complet) => {
    const msg = complet
      ? 'Confirmer que les travaux sont terminés et conformes ?'
      : 'Signaler une livraison partielle ?'
    if (!window.confirm(msg)) return

    let reserves = ''
    if (!complet) {
      reserves = prompt('Décrivez les réserves ou travaux restants :') || ''
      if (!reserves.trim()) return
    }

    setSubmitting(true)
    try {
      await api.post(`/achats/${dossierId.current}/suivi/confirmer-livraison`, { complet, reserves })
      if (complet) {
        alert('Livraison confirmée ! Le dossier passe au statut LIVRÉ.')
        navigate(`/achats/dossiers/${numero}`)
      } else {
        await chargerDonnees()
      }
    } catch(e) {
      alert(e.response?.data?.message || 'Erreur')
    } finally {
      setSubmitting(false)
    }
  }

  // Styles réutilisables
  const cardStyle = {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '16px',
    border: '1px solid #e0e0e0'
  }

  const btnStyle = (color) => ({
    background: color,
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    cursor: submitting ? 'not-allowed' : 'pointer',
    opacity: submitting ? 0.7 : 1,
    fontSize: '14px',
    fontWeight: '600'
  })

  if (loading) return (
    <div>
      <Header utilisateur={utilisateur} onDeconnexion={deconnexion} />
      <div style={{ textAlign: 'center', padding: '60px', color: '#999' }}>
        Chargement du suivi...
      </div>
    </div>
  )

  if (error) return (
    <div>
      <Header utilisateur={utilisateur} onDeconnexion={deconnexion} />
      <div style={{ textAlign: 'center', padding: '60px', color: '#f44336' }}>
        {error}
      </div>
    </div>
  )

  const { dossier, rapports, stats } = data

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      <Header utilisateur={utilisateur} onDeconnexion={deconnexion} />

      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>

        {/* Bouton retour */}
        <button
          onClick={() => navigate(`/achats/dossiers/${numero}`)}
          style={{
            background: 'none', border: '1px solid #ddd',
            borderRadius: '6px', padding: '6px 14px',
            cursor: 'pointer', fontSize: '13px',
            color: '#555', marginBottom: '20px',
            display: 'inline-flex', alignItems: 'center', gap: '6px'
          }}
        >
          ← Retour au dossier
        </button>

        {/* En-tête */}
        <div style={{ ...cardStyle, borderLeft: '4px solid #1976d2' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <div style={{ fontWeight: '700', fontSize: '18px', color: '#1a237e' }}>
                📊 Suivi d'exécution
              </div>
              <div style={{ color: '#555', fontSize: '14px', marginTop: '4px' }}>
                {dossier.numero} — {dossier.statut}
              </div>
            </div>

            {/* Définir date limite */}
            {ROLES_PEUVENT_GERER.includes(monRole) && (
              <button
                onClick={() => setShowFormDateLimite(!showFormDateLimite)}
                style={{
                  background: '#f5f5f5', border: '1px solid #ddd',
                  borderRadius: '6px', padding: '8px 14px',
                  cursor: 'pointer', fontSize: '13px', color: '#555'
                }}
              >
                📅 {stats.date_limite ? 'Modifier date limite' : 'Définir date limite'}
              </button>
            )}
          </div>

          {/* Formulaire date limite */}
          {showFormDateLimite && (
            <div style={{
              marginTop: '12px', padding: '14px',
              background: '#f5f5f5', borderRadius: '8px',
              display: 'flex', gap: '10px', alignItems: 'center'
            }}>
              <input
                type="date"
                value={formDateLimite}
                onChange={e => setFormDateLimite(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
              />
              <button onClick={handleDefinirDateLimite} style={btnStyle('#1976d2')}>
                Confirmer
              </button>
              <button onClick={() => setShowFormDateLimite(false)}
                style={{ ...btnStyle('#757575') }}>
                Annuler
              </button>
            </div>
          )}
        </div>

        {/* Alerte retard */}
        {stats.en_retard && (
          <div style={{
            background: '#ffebee', border: '2px solid #f44336',
            borderRadius: '12px', padding: '16px 20px', marginBottom: '16px'
          }}>
            <div style={{ fontWeight: '700', color: '#c62828', fontSize: '15px' }}>
              🔴 TRAVAUX EN RETARD — {stats.jours_retard} jour(s) de dépassement
            </div>
            <div style={{ color: '#666', fontSize: '13px', marginTop: '4px' }}>
              Date limite : {new Date(stats.date_limite).toLocaleDateString('fr-FR')} ·
              Aujourd'hui : {new Date().toLocaleDateString('fr-FR')}
            </div>
          </div>
        )}

        {/* Progression */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div style={{ fontWeight: '600', fontSize: '15px' }}>
              Avancement global
            </div>
            <div style={{ fontWeight: '700', fontSize: '18px', color: stats.pourcentage >= 100 ? '#4caf50' : '#1976d2' }}>
              {stats.pourcentage}%
            </div>
          </div>

          {/* Barre de progression */}
          <div style={{
            height: '16px', background: '#e0e0e0',
            borderRadius: '8px', overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              width: `${stats.pourcentage}%`,
              background: stats.en_retard ? '#f44336' : stats.pourcentage >= 100 ? '#4caf50' : '#1976d2',
              borderRadius: '8px',
              transition: 'width 0.3s ease'
            }} />
          </div>

          {/* Info jours */}
          {stats.date_limite && (
            <div style={{ marginTop: '10px', fontSize: '13px', color: '#666' }}>
              {stats.en_retard ? (
                <span style={{ color: '#f44336', fontWeight: '600' }}>
                  ⚠️ {stats.jours_retard} jour(s) de retard
                </span>
              ) : stats.jours_restants !== null ? (
                <span style={{ color: stats.jours_restants <= 3 ? '#ff9800' : '#4caf50', fontWeight: '600' }}>
                  ⏱️ {stats.jours_restants} jour(s) restant(s)
                  · Date limite : {new Date(stats.date_limite).toLocaleDateString('fr-FR')}
                </span>
              ) : null}
            </div>
          )}
        </div>

        {/* Bouton rapport du jour */}
        {ROLES_PEUVENT_SAISIR.includes(monRole) && dossier.statut === 'EN_EXECUTION' && (
          <div style={{ marginBottom: '16px' }}>
            {!showFormRapport ? (
              <button
                onClick={() => {
                  setFormRapport(f => ({ ...f, pourcentage_avancement: stats.pourcentage }))
                  setShowFormRapport(true)
                }}
                style={btnStyle('#1976d2')}
              >
                + Rapport du jour
              </button>
            ) : (
              <div style={cardStyle}>
                <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '16px' }}>
                  📝 Rapport du {new Date().toLocaleDateString('fr-FR')}
                </div>

                {/* Pourcentage */}
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '6px' }}>
                    Avancement : {formRapport.pourcentage_avancement}%
                  </label>
                  <input
                    type="range"
                    min="0" max="100" step="5"
                    value={formRapport.pourcentage_avancement}
                    onChange={e => setFormRapport({ ...formRapport, pourcentage_avancement: parseInt(e.target.value) })}
                    style={{ width: '100%', cursor: 'pointer' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#888' }}>
                    <span>0%</span><span>50%</span><span>100%</span>
                  </div>
                </div>

                {/* Commentaire */}
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '6px' }}>
                    Commentaire * (travaux effectués aujourd'hui)
                  </label>
                  <textarea
                    value={formRapport.commentaire}
                    onChange={e => setFormRapport({ ...formRapport, commentaire: e.target.value })}
                    placeholder="Ex: Pose des dalles terminée, peinture commencée..."
                    rows={3}
                    style={{
                      width: '100%', padding: '10px',
                      borderRadius: '8px', border: '1px solid #ddd',
                      fontSize: '13px', resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Problèmes */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '6px' }}>
                    Problèmes signalés (optionnel)
                  </label>
                  <textarea
                    value={formRapport.problemes_signales}
                    onChange={e => setFormRapport({ ...formRapport, problemes_signales: e.target.value })}
                    placeholder="Ex: Manque de ciment, retard livraison matériaux..."
                    rows={2}
                    style={{
                      width: '100%', padding: '10px',
                      borderRadius: '8px', border: '1px solid #ddd',
                      fontSize: '13px', resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={handleSoumettreRapport} style={btnStyle('#388e3c')}>
                    💾 Soumettre le rapport
                  </button>
                  <button onClick={() => setShowFormRapport(false)} style={btnStyle('#757575')}>
                    Annuler
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Confirmer livraison */}
        {ROLES_PEUVENT_SAISIR.includes(monRole) && dossier.statut === 'EN_EXECUTION' && stats.pourcentage >= 80 && (
          <div style={{
            ...cardStyle,
            border: '2px solid #4caf50',
            background: '#f1f8e9'
          }}>
            <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '12px', color: '#2e7d32' }}>
              ✅ Les travaux sont-ils terminés ?
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button onClick={() => handleConfirmerLivraison(true)} style={btnStyle('#388e3c')}>
                ✅ Oui, travaux terminés et conformes
              </button>
              <button onClick={() => handleConfirmerLivraison(false)} style={btnStyle('#ff9800')}>
                ⚠️ Non, livraison partielle
              </button>
            </div>
          </div>
        )}

        {/* Liste des rapports */}
        <div style={cardStyle}>
          <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '16px' }}>
            📋 Rapports d'exécution ({rapports.length})
          </div>

          {rapports.length === 0 ? (
            <div style={{ color: '#999', fontStyle: 'italic', fontSize: '13px', textAlign: 'center', padding: '20px' }}>
              Aucun rapport saisi pour l'instant
            </div>
          ) : (
            rapports.map((r, i) => (
              <div key={r.id} style={{
                padding: '14px',
                borderRadius: '8px',
                marginBottom: '10px',
                background: i === 0 ? '#e8f5e9' : '#f9f9f9',
                border: i === 0 ? '1px solid #a5d6a7' : '1px solid #e0e0e0'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div style={{ fontWeight: '600', fontSize: '13px', color: '#333' }}>
                    {r.auteur_nom || r.auteur_nom_complet}
                    {i === 0 && (
                      <span style={{
                        marginLeft: '8px', background: '#4caf50',
                        color: 'white', fontSize: '10px',
                        padding: '2px 6px', borderRadius: '10px'
                      }}>
                        DERNIER
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: '#888' }}>
                    {new Date(r.date_rapport).toLocaleDateString('fr-FR')}
                  </div>
                </div>

                {/* Barre mini */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  <div style={{
                    flex: 1, height: '8px',
                    background: '#e0e0e0', borderRadius: '4px', overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${r.pourcentage_avancement}%`,
                      background: '#1976d2', borderRadius: '4px'
                    }} />
                  </div>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#1976d2', minWidth: '35px' }}>
                    {r.pourcentage_avancement}%
                  </span>
                </div>

                {r.commentaire && (
                  <div style={{ fontSize: '13px', color: '#444', marginBottom: r.problemes_signales ? '6px' : 0 }}>
                    {r.commentaire}
                  </div>
                )}

                {r.problemes_signales && (
                  <div style={{
                    fontSize: '12px', color: '#e65100',
                    background: '#fff3e0', padding: '6px 10px',
                    borderRadius: '4px', marginTop: '6px'
                  }}>
                    ⚠️ {r.problemes_signales}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

      </div>
    </div>
  )
}

export default SuiviExecutionPage
